import React, { lazy, useEffect, useRef, useState } from "react";
import {
  View,
  Platform,
  Pressable,
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
  ScrollView,
  ActivityIndicator,
} from "react-native";
import { useDeviceContext } from "twrnc";

import Icon from "react-native-vector-icons/FontAwesome";

import { tw, colors } from "@/lib";
import { Input, InputProps } from "react-native-elements";
import { Text } from "@/components/Commons";

export interface DropDownItem {
  label: string;
  value: string;
  metaInfo?: any;
}

export const CustomDropdown = (
  props: InputProps & {
    lazy?: boolean;
    loading?: boolean;
    data?: DropDownItem[];
    minSearchLength?: number;
    onSelect?: (input: string, metaInfo?: any) => void;
  }
) => {
  useDeviceContext(tw);

  const menu = useRef<any>(null);
  const icon = useRef<any>(null);
  const input = useRef<any>(null);

  const { data } = props;
  const minSearchLength = props.length || 5;

  const [filtering, setFiltering] = useState<boolean>(true);

  const [state, setState] = useState({ isFocused: false, onEdit: false });

  const [value, setValue] = useState({ value: props.value || "" });

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [dataOptions, setDataOptions] = useState<DropDownItem[] | undefined>();

  useEffect(() => {
    setValue({
      value:
        data?.find((e) => e.value == props.value)?.label || props.value || "",
    });
  }, [props.value]);

  const focus = () => {
    if(!props.disabled) {
      setState({ isFocused: true, onEdit: false });
      input.current?.focus();
    }
  };

  const blur = () => {
    if(!props.disabled) {
      setState({ isFocused: false, onEdit: false });
      input.current?.blur();
    }
  };

  const onEditValue = () => setState({ ...state, onEdit: true });

  const onUpdateValue = (input: string) => {
    setValue({ value: input });
    if (props.onChangeText) {
      props.onChangeText(input);
    }
  };

  const keyHandled = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    switch (e.nativeEvent.key) {
      case "ArrowDown":
        setSelectedIndex(Math.min(selectedIndex + 1, dataOptions!.length - 1));
        e.preventDefault();
        break;

      case "ArrowUp":
        setSelectedIndex(Math.max(selectedIndex - 1, 0));
        e.preventDefault();
        break;
      case "Enter":
        if (selectedIndex > -1) {
          onUpdateValue(dataOptions![selectedIndex].label);
        }
        setSelectedIndex(-1);

      default:
        setSelectedIndex(-1);

        break;
    }
  };

  const loadDataOptions = () => {
    setFiltering(true);
    if (state.onEdit) {
      const info = data?.filter((e: DropDownItem) => {
        return e.label.toLowerCase().includes(value.value.toLowerCase());
      });
      setDataOptions(info);
    } else {
      setDataOptions(data);
    }
    setFiltering(false);
  };

  useEffect(() => {
    loadDataOptions();
  }, [value.value, state.onEdit, data]);

  const values = dataOptions?.map((e) => e.value);

  const itemRenderer = ({
    item,
    onSelect,
  }: {
    item: DropDownItem;
    onSelect: (item: DropDownItem) => void;
  }) => {
    return (
      <Pressable
        key={item.value}
        style={tw.style(
          "py-2",
          "px-2"
          // {
          //   "bg-blue-300": item.value == values?.at(selectedIndex),
          // }
        )}
        onPress={(e) => (e.preventDefault(), onSelect(item))}
        focusable={false}
      >
        <Text>{item.label}</Text>
      </Pressable>
    );
  };

  const showInsideContent = () => {
    if (value?.value?.length >= minSearchLength) {
      if (props.loading) {
        return (
          <View style={tw`my-4`}>
            <ActivityIndicator size="large" />
          </View>
        );
      }

      if (data != undefined && data.length > 0) {
        return data.map((item: DropDownItem) =>
          itemRenderer({
            item,
            onSelect: (input) => {
              setValue({ value: input.label });
              if (props.onSelect) props.onSelect(item.value, item.metaInfo);

              blur();
            },
          })
        );
      }

      if (data == undefined || data.length == 0) {
        return <Text weight="semiBold"> No matching options </Text>;
      }
    } else {
      return (
        <Text weight="semiBold">Introduce at least {minSearchLength} letters to search </Text>
      );
    }
  };

  return (
    <Pressable
      style={[tw`w-full`, { zIndex: "auto" }]}
      focusable={false}
      ref={icon}
      disabled={props.disabled}
    >
      <Input
        ref={input}
        autoCompleteType={undefined}
        editable={!props.disabled}
        focusable={!props.disabled}
        disabled={props.disabled}
        {...{ ...props, style: null }}
        value={value.value}
        rightIcon={
          <Icon
            name={state.isFocused ? "chevron-up" : "chevron-down"}
            // @ts-ignore
            type="font-awesome"
            color="#9CA3AF"
            disabled={props.disabled}
            onPress={state.isFocused ? blur : focus}
          />
        }
        labelStyle={tw`font-normal text-input-label text-gray-500`}
        inputContainerStyle={[
          tw.style(
            "w-full",
            "h-input",
            "border",
            "border-gray-lightest" ,
            "rounded-3",
            "px-2",
            "bg-white",
            state.isFocused ? "rounded-b-0" : ""
          ),
          {
            backgroundColor: props.disabled
              ? colors.grayLightest
              : colors.white,
            borderColor: colors.grayLightest,
          },
          props.style,
        ]}
        // @ts-ignore
        inputStyle={Platform.select({
          web: {
            outline: "none",
            fontFamily: "Roboto_400Regular",
            fontSize: 14,
          },
        })}
        containerStyle={tw`h-[88px] px-2`}
        onFocus={focus}
        onBlur={(e) => {
          let noBlur = false;
          menu.current?.childNodes.forEach((i: any) => {
            //@ts-ignore
            if (i == e.relatedTarget) noBlur = true;
          });
          //@ts-ignore
          noBlur = noBlur || e.relatedTarget == icon.current;
          if (!noBlur) {
            e.preventDefault();
            blur();
          }
        }}
        onChangeText={onUpdateValue}
        onChange={onEditValue}
        onKeyPress={keyHandled}
      />

      <View style={tw`px-2 z-50`}>
        <View style={tw`relative`}>
          <View
            ref={menu}
            style={[
              tw.style([
                "bg-white",
                state.isFocused ? "absolute max-h-[150px] " : "hidden",
                "w-full",
                "border-blue-600",
                "border",
                "border-gray-lightest",
                "-top-7",
              ]),
              { overflowY: "scroll" },
            ]}
          >
            {showInsideContent()}
          </View>
        </View>
      </View>
    </Pressable>
  );
};
